import { hash } from '@ember/helper';
/* eslint-disable ember/no-computed-properties-in-native-classes */
import { action, computed, set } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { type Registry as Services, service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBadge,
  PrismButton,
  PrismCheckbox,
  PrismIcon,
  PrismInput,
  PrismLoadingBar,
  PrismModal,
  PrismSelectInput,
  PrismTable,
} from '@mylighthouse/prism-ember';
import { task, timeout } from 'ember-concurrency';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import type { User } from 'frontend-utils/models';
import GeneralDropdownsMultipleSelectDropdownAjax from 'lumen/components/general/dropdowns/multiple-select-dropdown-ajax';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import SettingsHotelAccessFormsAccessRightsCheckbox from 'lumen/components/settings/hotel-access/forms/access-rights-checkbox';
import SettingsHotelAccessFormsAccessRightsDate from 'lumen/components/settings/hotel-access/forms/access-rights-date';
import SettingsHotelAccessFormsAccessRightsDeleteCta from 'lumen/components/settings/hotel-access/forms/access-rights-delete-cta';
import SettingsHotelAccessFormsAccessRightsDropdown from 'lumen/components/settings/hotel-access/forms/access-rights-dropdown';
import SettingsHotelAccessFormsAccessRightsHotelName from 'lumen/components/settings/hotel-access/forms/access-rights-hotel-name';
import SettingsHotelAccessFormsCellHeader from 'lumen/components/settings/hotel-access/forms/cell-header';
import { ACCESS_RIGHTS } from 'lumen/utils/constants/general-constants';

interface SettingsHotelAccessFormsFormUserEditSignature {
  Args: {
    changeHotelSearch: unknown;
    editText: unknown;
    model: unknown;
    redirectOnClose: unknown;
    refreshData: unknown;
    sortResults: unknown;
    userId: unknown;
  };
}

export default class SettingsHotelAccessFormsFormUserEdit extends Component<SettingsHotelAccessFormsFormUserEditSignature> {
  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  @service declare alerting: Services['alerting'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];

  @tracked convertedModel = [];
  @tracked hasUnsavedRateInsightBulkEditValues = false;
  @tracked hasUnsavedRevenueInsightBulkEditValues = false;
  // Bulk edit mode variables
  @tracked hasUnsavedRightBulkEditValues = false;
  @tracked hotelOptions = [];
  @tracked hotels = [];
  @tracked page = 0;
  // States of checkboxes for permission
  @tracked rateInsight = false;
  @tracked rateInsightIndeterminate = false;
  @tracked revenueInsight = false;
  @tracked revenueInsightIndeterminate = false;
  // Multi rights
  @tracked rights = null;
  @tracked rowSelection = [];
  @tracked searchText = '';
  @tracked selectAll = false;
  @tracked selectedRight = null;
  @tracked showSuccessMessage = false;
  @tracked sorts = [
    {
      // @ts-expect-error: Incorrect type
      valuePath: this.args.model?.sortBy === 'name' ? 'name' : 'date',
      // @ts-expect-error: Incorrect type
      isAscending: this.args.model?.sortOrder === 'asc',
    },
  ];
  @tracked totalCount = 0;
  @tracked visibleItems = 0;

  errorMessages = [];
  loadMoreDebounce = null;

  getAutocompleteHotelsTask = task({ keepLatest: true }, async (options) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const page = options?.page ?? 0;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const query = options?.query ?? '';
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const userId = options?.userId;

    const data = {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      page,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      q: query,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      user: userId,
    };

    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { meta, results } = await this.ajax.request(
        'autocomplete/subscription_new_user',
        {
          data,
          method: 'GET',
        },
      );

      const newOptions = this.hotelAndMarketService
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        .formatAutocompleteResults(results)
        .map((hotel) => {
          const { hotel_id, name, products, subscription_type } = hotel;

          return {
            badge: subscription_type,
            description: products.join(', '),
            id: hotel_id,
            name,
          };
        });

      // @ts-expect-error: Incorrect type
      this.hotelOptions =
        page === 0 ? newOptions : [...this.hotelOptions, ...newOptions];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      this.totalCount = meta.results_count;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      this.visibleItems = meta.results_per_page;
    } catch {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert({
        title: this.intl.t('settings.hotel_access.update_failed'),
      });
    }
  });

  loadMoreUHRTask = task(
    { keepLatest: true },
    async (page = 0, setSelectAllToFalse = false) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.page = page;

      if (
        this.page === 0 ||
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.args.model.userhotelrelationships.length <
          // @ts-expect-error: Incorrect type
          this.args.model.totalResults
      ) {
        // Added the assignment of the boolean to prevent the "You modified twice in one runloop"-error
        // @ts-expect-error: Incorrect type
        if (!this.args.model?.isLoading) {
          set(this, 'args.model.isLoading', true);
        }
        const apiParams = {
          user_id: this.args.userId,
          page: this.page,
        };

        // @ts-expect-error: Incorrect type
        if (this.args.model?.searchTerm !== '') {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          apiParams.q = this.args.model?.searchTerm;
        }

        // @ts-expect-error: Incorrect type
        if (this.args.model?.searchTerm !== '' && this.page === 0) {
          this.setSelectAll(false);
        }
        // @ts-expect-error: Incorrect type
        if (this.args.model?.sortBy) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          apiParams.sort_by = this.args.model?.sortBy;
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          apiParams.sort_order =
            // @ts-expect-error: Incorrect type
            this.args.model?.sortOrder !== ''
              ? // @ts-expect-error: Incorrect type
                this.args.model?.sortOrder
              : 'asc';
        }
        if (this.page <= 0 || this.page === undefined) {
          // @ts-expect-error: Incorrect type
          set(this.args.model, 'userhotelrelationships', []);
          this.resetModel();
        }

        await this.store
          // @ts-expect-error: Incorrect type
          .query('userhotelrelationship', apiParams)
          .then((content) => {
            if (this.args.model) {
              set(this.args.model, 'userhotelrelationships', [
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                ...(this.args.model?.userhotelrelationships ?? []),
                ...content,
              ]);
            }

            if (this.selectAll && !setSelectAllToFalse) {
              this.setSelectAll(true);

              this.syncRateInsightAccessStatus();

              this.syncRevenueInsightAccessStatus();

              this.syncRightsStatus();
            }

            set(
              this,
              'args.model.totalPages',
              Math.floor(
                // @ts-expect-error: Incorrect type
                content.meta.results_count / content.meta.results_per_page,
              ) + 1,
            );
            // @ts-expect-error: Incorrect type
            set(this, 'args.model.totalResults', content.meta.results_count);
            set(
              this,
              'args.model.totalResultsUnlocked',
              // @ts-expect-error: Incorrect type
              content.meta.results_count_unlocked,
            );
            set(this, 'args.model.isLoading', false);
            set(this, 'args.model.searchTerm', this.searchText);
          });
      }
    },
  );
  showSuccessMessageTask = task({ drop: true }, async () => {
    this.showSuccessMessage = true;
    await timeout(6000);
    this.showSuccessMessage = false;
  });

  // Listen on model loading state because for the initial run the user might not be in the datastore yet
  @computed('args.{userId,model.isLoading}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get canAddHotels() {
    // @ts-expect-error: Incorrect type
    const result = this.store.peekRecord<User>('user', this.args.userId);

    let canAddHotels = false;

    if (result?.isEditable) {
      canAddHotels = true;
    }

    return canAddHotels;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    const columns = [
      {
        name: this.intl.t('useraccess_rights_header_dateadded'),
        tdComponent: SettingsHotelAccessFormsAccessRightsDate,
        columnKey: 'date',
        valuePath: 'date',
        displayValuePath: 'createdAt',
        minWidth: 145,
        maxWidth: 145,
        thClassNames: 'text-data',
        tdClassNames: 'text-data text-data--border-right',
        isSortable: this.isSortable,
        hideable: false,
      },
      {
        name: this.intl.t('rates.hotelname'),
        thComponent: SettingsHotelAccessFormsCellHeader,
        tdComponent: SettingsHotelAccessFormsAccessRightsHotelName,
        columnKey: 'name',
        valuePath: 'name',
        thClassNames: 'text-data',
        tdClassNames: 'text-data text-data--border-right',
        isSortable: this.isSortable,
        hideable: false,
        width: 200,
      },
      {
        name: this.intl.t('useraccess_rights_tableheader_rights'),
        tdComponent: SettingsHotelAccessFormsAccessRightsDropdown,
        valuePath: 'accessRight',
        thClassNames: 'text-data',
        tdClassNames: 'text-data',
        width: 230,
        isSortable: false,
        hideable: false,
      },
      {
        name: this.intl.t('useraccess_rights_tableheader_rate_market_insight'),
        tdComponent: SettingsHotelAccessFormsAccessRightsCheckbox,
        thComponent: SettingsHotelAccessFormsCellHeader,
        valuePath: 'products',
        columnProductType: 'rate_insight',
        thClassNames: 'numeric-data',
        isSortable: false,
        hideable: false,
        width: 170,
      },
      {
        name: this.intl.t('useraccess_rights_tableheader_bi_benchmarking'),
        thComponent: SettingsHotelAccessFormsCellHeader,
        tdComponent: SettingsHotelAccessFormsAccessRightsCheckbox,
        valuePath: 'products',
        columnProductType: 'revenue_insight',
        thClassNames: 'numeric-data',
        isSortable: false,
        hideable: false,
        tooltip: this.intl.t(
          'useraccess_rights_tableheader_bi_benchmarking.tooltip',
        ),
        width: 170,
      },
      {
        name: '',
        tdComponent: SettingsHotelAccessFormsAccessRightsDeleteCta,
        thClassNames: 'text-data',
        tdClassNames: 'delete text-data--border-left',
        valuePath: 'isDeletable',
        maxWidth: 25,
        isSortable: false,
        hideable: false,
      },
    ];
    return columns;
  }

  @computed(
    'args.model.userhotelrelationships',
    'rowSelection',
    'rowSelection.length',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get excludedResults() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.args.model?.userhotelrelationships
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .filter((item) => {
          return !this.rowSelection.some((selectedItem) => {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            return selectedItem.subscriptionId === item.subscriptionId;
          });
        })
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        .map((item) => item.subscriptionId)
    );
  }

  @computed('args.model.userhotelrelationships.length')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasResults() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return this.args.model?.userhotelrelationships?.length > 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get inEditMode() {
    return (
      this.rowSelection.length > 0 &&
      (this.hasUnsavedRightBulkEditValues ||
        this.hasUnsavedRateInsightBulkEditValues ||
        this.hasUnsavedRevenueInsightBulkEditValues)
    );
  }

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'args.model.isLoading',
    'args.model.userhotelrelationships.length',
    'page',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLoading() {
    // Shows table loading state only on the initial load of data
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      this.page === 0 &&
      // @ts-expect-error: Incorrect type
      this.args.model?.isLoading &&
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.model?.userhotelrelationships.length === 0
    );
  }

  @computed('args.model.isLoading', 'page')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLoadingMore() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.page > 0 && this.args.model?.isLoading;
  }

  // Check which right is selected in the bulk edit, and disable the product checkboxes when it's
  // admin, or when a selected row can't have this product toggled (e.g. it's the last accessible
  // product on that row). Only the rows currently selected/visible on the FE can be checked; the
  // backend independently skips any other row where the update would leave it with no accessible
  // product.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRateInsightBulkCheckboxDisabled() {
    return (
      // @ts-expect-error: Incorrect type
      this.selectedRight.key === 'admin' ||
      this.rowSelection.some((item) =>
        this.isAccessRightDisabled(item, 'rate_insight'),
      )
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRevenueInsightBulkCheckboxDisabled() {
    return (
      // @ts-expect-error: Incorrect type
      this.selectedRight.key === 'admin' ||
      this.rowSelection.some((item) =>
        this.isAccessRightDisabled(item, 'revenue_insight'),
      )
    );
  }

  // @computed consumer `showNoResults` needs this via the classic dep-key chain
  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSearchTextTooLong() {
    return (this.searchText ?? '').length > 100;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSelf() {
    return this.args.userId === this.hotelAndMarketService.user?.id;
  }

  @computed('args.model.searchTerm')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSortable() {
    // @ts-expect-error: Incorrect type
    return isEmpty(this.args.model?.searchTerm);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get noResultsMessage() {
    return this.showNoResults
      ? this.intl.t('general.no_results').toString()
      : undefined;
  }

  @computed(
    'args.model.userhotelrelationships.[]',
    'onProductChange',
    'onRightsChange',
    'onSubscriptionDelete',
    'rowSelection',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    // @ts-expect-error: Incorrect type
    const rows = [];
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.args.model?.userhotelrelationships.forEach((item) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      item.onProductChange = this.onProductChange.bind(this);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      item.onRightsChange = this.onRightsChange.bind(this);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      item.onSubscriptionDelete = this.onSubscriptionDelete.bind(this);
      const isSelected = this.rowSelection.some(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        (selectedItem) => selectedItem.subscriptionId === item.subscriptionId,
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      set(item, 'isSelected', isSelected);

      rows.push(item);
    });
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return rows;
  }

  @computed(
    'selectAll',
    'rowSelection.length',
    'args.model.totalResultsUnlocked',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedRowCount() {
    if (this.selectAll) {
      // Because locked UHRs can't be selected, only use unlocked UHRs to calculate the total number of selected rows
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return this.args.model.totalResultsUnlocked;
    }
    return this.rowSelection?.length;
  }

  @computed('rowSelection.length')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showMultipleSelected() {
    return this.rowSelection?.length > 0;
  }

  @computed('hasResults', 'args.model.isLoading', 'isSearchTextTooLong')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showNoResults() {
    return (
      // @ts-expect-error: Incorrect type
      (!this.args.model.isLoading && !this.hasResults) ||
      this.isSearchTextTooLong
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showRateInsight() {
    let visible = false;

    if (!this.selectAll) {
      const items = this.rowSelection;

      items.forEach((item) => {
        if (
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          item.products.rate_insight !== undefined &&
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          item.products.rate_insight.is_editable
        ) {
          visible = true;
        }
      });
    } else {
      visible = true;
    }
    return visible;
  }

  // Show the revenue checkbox when at least one selected line contains the rate product
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showRevenueInsight() {
    let visible = false;

    if (!this.selectAll) {
      const items = this.rowSelection;

      items.forEach((item) => {
        if (
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          item.products.revenue_insight &&
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          item.products.revenue_insight.is_editable
        ) {
          visible = true;
        }
      });
    } else {
      visible = true;
    }
    return visible;
  }

  constructor(
    // @ts-expect-error: Incorrect type
    owner,
    args: SettingsHotelAccessFormsFormUserEditSignature['Args'],
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    super(owner, args);

    // @ts-expect-error: Incorrect type
    this.rights = ACCESS_RIGHTS.map((right) => ({
      key: right.key,
      label: this.intl.t(right.value),
    }));
    // @ts-expect-error: Incorrect type
    this.selectedRight = {
      key: 'role',
      label: this.intl.t('useraccess_rights_role'),
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.loadMoreUHRTask.perform(0);

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.getAutocompleteHotelsTask.perform({
      userId: args.userId,
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.searchText = this.args.model.searchTerm;
  }

  checkRightsAndProducts(): void {
    // @ts-expect-error: Incorrect type
    if (!this.exclude) {
      const items = this.rowSelection;
      if (items.length > 0) {
        const firstItem = items[0];

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const initialRights = firstItem.accessRight;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const initialRateInsight =
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          firstItem.products?.rate_insight?.is_accessible;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const initialRevenueInsight =
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          firstItem.products?.revenue_insight?.is_accessible;

        let isRightsSame = true;
        let isRateInsightSame = true;
        let isRevenueInsightSame = true;

        items.forEach((item) => {
          if (isRightsSame) {
            // @ts-expect-error: Incorrect type
            isRightsSame = item.accessRight === initialRights;
          }
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (isRateInsightSame && item.products.rate_insight !== undefined) {
            isRateInsightSame =
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              item.products.rate_insight.is_accessible === initialRateInsight;
          }
          if (
            isRevenueInsightSame &&
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            item.products.revenue_insight !== undefined
          ) {
            isRevenueInsightSame =
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              item.products.revenue_insight.is_accessible ===
              initialRevenueInsight;
          }
        });

        if (isRightsSame) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
          this.selectedRight = this.rights.find(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            (item) => item.key === initialRights,
          );
        } else {
          // @ts-expect-error: Incorrect type
          this.selectedRight = {
            key: 'role',
            label: this.intl.t('useraccess_rights_role'),
          };
        }

        if (isRateInsightSame) {
          this.rateInsightIndeterminate = false;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          this.rateInsight = initialRateInsight;
        } else {
          this.rateInsightIndeterminate = true;
        }

        if (isRevenueInsightSame) {
          this.revenueInsightIndeterminate = false;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          this.revenueInsight = initialRevenueInsight;
        } else {
          this.revenueInsightIndeterminate = true;
        }
      }
    }
  }

  /**
   * @param {Object} userhotelrelationship
   * @param {String} productType - 'rate_insight' | 'revenue_insight'
   */
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isAccessRightDisabled(userhotelrelationship, productType) {
    const accessibleProductCount = Object.values(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      userhotelrelationship.products,
      // @ts-expect-error: Incorrect type
    ).filter((product) => product.is_accessible).length;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const product = userhotelrelationship.products[productType];

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      userhotelrelationship.accessRight === 'admin' ||
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      !product?.is_editable ||
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (product?.is_accessible && accessibleProductCount === 1)
    );
  }

  /**
   * Clears the current model, so it can be fully rebuilt in the reset model function
   */
  resetModel(): void {
    this.convertedModel = [];
  }

  // @ts-expect-error: Incorrect type
  resetRowsToStateBeforeSelection(rows): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    rows.forEach((row) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const curentUserHotelRelationship =
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        this.args.model.userhotelrelationships.find(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          (item) => item.subscriptionId === row.subscriptionId,
        );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      curentUserHotelRelationship?.rollbackAttributes();
    });
  }

  sendRequest(
    // @ts-expect-error: Incorrect type
    data,
    // @ts-expect-error: Incorrect type
    method,
    reloadHotels = false,
    completeRefresh = true,
  ): void {
    this.ajax
      .request('userhotelrelationships/', {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        method,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        data,
      })
      .then(() => {
        if (completeRefresh) {
          this.store.unloadAll('userhotelrelationship');
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          this.loadMoreUHRTask.perform(0, true);
          // @ts-expect-error: Incorrect type
          this.args.refreshData();
          this.checkRightsAndProducts();
        } else {
          set(this, 'args.model.isLoading', false);
        }

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.showSuccessMessageTask.perform();

        if (reloadHotels) {
          const { userId } = this.args;

          // Placed the autocompleteHotels request to here to make sure it's fired after the update is done.
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          this.getAutocompleteHotelsTask.perform({ userId });
        }
      })
      .catch(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting
          .showErrorAlert({
            title: this.intl.t('settings.hotel_access.update_failed'),
          })
          .then(() => {
            // @ts-expect-error: Incorrect type
            // Refresh UHRs to fetch the latest state in the backend (prevent local frontend changes to keep showing)
            set(this.args.model, 'userhotelrelationships', []);
            this.resetModel();
            this.store.unloadAll('userhotelrelationship');
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.loadMoreUHRTask.perform(0, true);
          });
      });
  }

  // @ts-expect-error: Incorrect type
  setSelectAll(value): void {
    if (value) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.rowSelection = [
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        ...this.args.model.userhotelrelationships.filter(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          (item) => !item.isLocked,
        ),
      ];
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.selectAll = value;

    if (!value) {
      this.rowSelection = [];

      this.hasUnsavedRightBulkEditValues = false;
      this.hasUnsavedRateInsightBulkEditValues = false;
      this.hasUnsavedRevenueInsightBulkEditValues = false;
    }
  }

  // General functions
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  syncRateInsightAccessStatus() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const items = this.args.model.userhotelrelationships
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .filter((item) => !item.isLocked)
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .filter((item) => {
        return this.rowSelection.some(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          (selectedItem) => selectedItem.subscriptionId === item.subscriptionId,
        );
      });
    if (this.hasUnsavedRateInsightBulkEditValues) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      items.forEach((item) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const isDisabled = this.isAccessRightDisabled(item, 'rate_insight');

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (item.products?.rate_insight !== undefined && !isDisabled) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          set(item, 'products.rate_insight.is_accessible', this.rateInsight);
        }
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  syncRevenueInsightAccessStatus() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const items = this.args.model.userhotelrelationships
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .filter((item) => !item.isLocked)
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .filter((item) => {
        return this.rowSelection.some(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          (selectedItem) => selectedItem.subscriptionId === item.subscriptionId,
        );
      });
    if (this.hasUnsavedRevenueInsightBulkEditValues) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      items.forEach((item) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const isDisabled = this.isAccessRightDisabled(item, 'revenue_insight');

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (item.products?.revenue_insight !== undefined && !isDisabled) {
          set(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            item,
            'products.revenue_insight.is_accessible',
            this.revenueInsight,
          );
        }
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  syncRightsStatus() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const items = this.args.model.userhotelrelationships.filter((item) => {
      return this.rowSelection.some(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        (selectedItem) => selectedItem.subscriptionId === item.subscriptionId,
      );
    });

    // @ts-expect-error: Incorrect type
    if (this.selectedRight.key !== 'role') {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      items.forEach((item) => {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        set(item, 'accessRight', this.selectedRight.key.toLowerCase());
      });
    }
  }

  /**
   * This function is used to update the input values in the bulk edit block
   * It checks if the selected rows have the same access right and products
   * and updates the bulk edit selected value accordingly.
   */
  updateBulkEditSelectedValue(): void {
    const { rowSelection, rights, intl } = this;

    const allRateInsightChecked = rowSelection.every(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (item) => item.products.rate_insight?.is_accessible,
    );
    const allRateInsightUnchecked = rowSelection.every(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (item) => !item.products.rate_insight?.is_accessible,
    );
    this.rateInsight = allRateInsightChecked;
    this.rateInsightIndeterminate = !(
      allRateInsightChecked || allRateInsightUnchecked
    );

    const allRevenueInsightChecked = rowSelection.every(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (item) => item.products.revenue_insight?.is_accessible,
    );
    const allRevenueInsightUnchecked = rowSelection.every(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (item) => !item.products.revenue_insight?.is_accessible,
    );
    this.revenueInsight = allRevenueInsightChecked;
    this.revenueInsightIndeterminate = !(
      allRevenueInsightChecked || allRevenueInsightUnchecked
    );

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const firstAccessRight = rowSelection[0]?.accessRight;
    const isSameSelectedRight = rowSelection.every(
      // @ts-expect-error: Incorrect type
      (item) => item.accessRight === firstAccessRight,
    );
    if (isSameSelectedRight) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      this.selectedRight = rights.find(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        (item) => item.key === firstAccessRight,
      );
    } else {
      // @ts-expect-error: Incorrect type
      this.selectedRight = {
        key: 'role',
        label: intl.t('useraccess_rights_role'),
      };
    }
  }

  @action
  async bulkDelete(): Promise<void> {
    const confirmWasClicked = await this.alerting.showConfirmWarningAlert({
      text: this.intl.t('settings.hotel_access.confirm_bulk_delete'),
    });
    if (confirmWasClicked) {
      const selectedSubscriptions = this.rowSelection.map(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        (item) => item.subscriptionId,
      );

      const data = {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        subscription_ids: this.selectAll
          ? this.excludedResults
          : selectedSubscriptions,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        user_id: this.args.model?.userId,
        exclude: Boolean(this.selectAll),
      };

      if (this.searchText !== '' && this.selectAll) {
        // @ts-expect-error: Incorrect type
        data.q = this.searchText;
      }

      this.sendRequest(data, 'DELETE', true, false);

      this.setSelectAll(false);
    }
  }

  @action
  async cancelBulkEdit(): Promise<void> {
    const confirmWasClicked = await this.alerting.showConfirmWarningAlert({
      text: this.intl.t('settings.hotel_access.confirm_cancel_bulk_edit'),
    });
    if (confirmWasClicked) {
      this.resetRowsToStateBeforeSelection(this.rowSelection);
      this.setSelectAll(false);

      this.hasUnsavedRightBulkEditValues = false;
      this.hasUnsavedRateInsightBulkEditValues = false;
      this.hasUnsavedRevenueInsightBulkEditValues = false;
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  handleHotelsChange(options, { unsafe_isExcludeMode: exclude }): void {
    const { model, userId } = this.args;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.hotels = options;

    const data = {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      exclude,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      subscription_ids: this.hotels.map((hotel) => hotel.id),
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      user_id: model?.userId,
    };

    this.sendRequest(data, 'POST');

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.getAutocompleteHotelsTask.perform({ userId });
  }

  @action
  // @ts-expect-error: Incorrect type
  handleLoadMoreHotels(loadMore): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.getAutocompleteHotelsTask.perform({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      page: loadMore.page,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      query: loadMore.q,
      userId: this.args.userId,
    });
  }

  /**
   * Bulk edit Rights adjustment
   */
  @action
  // @ts-expect-error: Incorrect type
  multiRightsChanged(option): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.selectedRight = option;

    this.hasUnsavedRightBulkEditValues = true;

    this.syncRightsStatus();
  }

  @action
  onEndReached(): void {
    // @ts-expect-error: Incorrect type
    if (this.page + 1 < this.args.model.totalPages) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.loadMoreUHRTask.perform(this.page + 1);
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  onInput(event): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
    this.searchText = event.target.value;
    if (!this.isSearchTextTooLong) {
      set(this, 'args.model.searchTerm', this.searchText);
      // @ts-expect-error: Incorrect type
      this.args.changeHotelSearch(this.searchText);
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.loadMoreUHRTask.perform(0, true);
  }

  /**
   * onProductChange
   * @param  {} subscription
   */
  @action
  // @ts-expect-error: Incorrect type
  onProductChange(subscription): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const currentRecord = this.args.model.userhotelrelationships.filter(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (item) => item.subscriptionId === subscription.subscription,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    )[0];
    if (currentRecord !== undefined) {
      set(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        currentRecord,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        `products.${subscription.type}.is_accessible`,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        subscription.value,
      );
      const products = {};
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      Object.keys(currentRecord.products).forEach((key) => {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        products[key] = currentRecord.products[key].is_accessible;
      });
      const data = {
        accessible_products: products,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        subscription_ids: [subscription.subscription],
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        user_id: this.args.model?.userId,
      };

      this.sendRequest(data, 'PUT', false, false);
      this.setSelectAll(false);
    }
  }

  // @ts-expect-error: Incorrect type
  @action async onRightsChange(subscription): Promise<void> {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const currentRecord = this.args.model.userhotelrelationships.filter(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (item) => item.subscriptionId === subscription.subscription,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    )[0];

    if (currentRecord !== undefined) {
      // When editing youself, ask for a confirmation
      if (this.isSelf) {
        const confirmWasClicked = await this.alerting.showConfirmWarningAlert({
          text: this.intl.t('settings.hotel_access.confirm_edit_self'),
        });
        if (!confirmWasClicked) {
          return;
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      set(currentRecord, 'accessRight', subscription.value.toLowerCase());

      // When switching to admin access, access to all products is granted
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (subscription.value === 'admin') {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        Object.keys(currentRecord.products).forEach((product) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
          set(currentRecord.products[product], 'is_accessible', true);
        });
      }

      const data = {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        access_right: subscription.value.toLowerCase(),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        subscription_ids: [subscription.subscription],
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        user_id: this.args.model?.userId,
      };

      this.sendRequest(data, 'PUT', false, false);
      this.setSelectAll(false);
    }
  }

  // @ts-expect-error: Incorrect type
  @action async onSubscriptionDelete(subscriptionId): Promise<void> {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const currentRecord = this.args.model.userhotelrelationships.filter(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (item) => item.subscriptionId === subscriptionId,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    )[0];

    if (currentRecord !== undefined) {
      const confirmWasClicked = await this.alerting.showConfirmWarningAlert({
        text: this.intl.t('settings.hotel_access.confirm_delete', {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          record: currentRecord.subscriptionName,
        }),
      });
      if (confirmWasClicked) {
        const data = {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          subscription_ids: [currentRecord.subscriptionId],
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          user_id: this.args.model?.userId,
        };
        this.store.unloadRecord(currentRecord);
        set(
          // @ts-expect-error: Incorrect type
          this.args.model,
          'userhotelrelationships',
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          this.args.model.userhotelrelationships.filter(
            // @ts-expect-error: Incorrect type
            (item) => item !== currentRecord,
          ),
        );
        this.sendRequest(data, 'DELETE', true, false);
        this.setSelectAll(false);
      }
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  onUpdateSorts(sorts): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (sorts.length > 1) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.sorts = sorts;
      return;
    }

    const hasExistingSort = this.sorts.length !== 0;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const isDefaultSort = sorts.length === 0;

    if (hasExistingSort && isDefaultSort) {
      const newSorts = [...this.sorts];
      // @ts-expect-error: Incorrect type
      newSorts[0].isAscending = !newSorts[0].isAscending;

      this.sorts = newSorts;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-assignment
      this.sorts = [...sorts];
    }

    // @ts-expect-error: Incorrect type
    const columnKey = this.sorts[0].valuePath;
    // @ts-expect-error: Incorrect type
    const ascending = this.sorts[0].isAscending;
    const column = this.columns.find((col) => col.columnKey === columnKey);
    // @ts-expect-error: Incorrect type
    column.ascending = ascending;

    // @ts-expect-error: Incorrect type
    this.args.sortResults(column);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.loadMoreUHRTask.perform(0, true);
  }

  @action
  saveBulkEdit(): void {
    set(this, 'args.model.isLoading', true);

    const products = {};

    if (!this.rateInsightIndeterminate) {
      // @ts-expect-error: Incorrect type
      products.rate_insight = this.rateInsight;
    }
    if (!this.revenueInsightIndeterminate) {
      // @ts-expect-error: Incorrect type
      products.revenue_insight = this.revenueInsight;
    }

    const selectedSubscriptions = this.rowSelection.map(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      (item) => item.subscriptionId,
    );

    const data = {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      access_right: this.selectedRight.key,
      accessible_products: products,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      subscription_ids: this.selectAll
        ? this.excludedResults
        : selectedSubscriptions,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      user_id: this.args.model?.userId,
      exclude: Boolean(this.selectAll),
    };

    if (this.searchText !== '' && this.selectAll) {
      // @ts-expect-error: Incorrect type
      data.q = this.searchText;
    }

    this.setSelectAll(false);
    this.sendRequest(data, 'PUT', false, false);

    this.hasUnsavedRightBulkEditValues = false;
    this.hasUnsavedRateInsightBulkEditValues = false;
    this.hasUnsavedRevenueInsightBulkEditValues = false;
  }

  @action
  // @ts-expect-error: Incorrect type
  setRowSelection(rowSelection): void {
    const deselectedRows = this.rowSelection.filter(
      (item) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        !rowSelection.some(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          (selectedItem) => selectedItem.subscriptionId === item.subscriptionId,
        ),
    );

    this.resetRowsToStateBeforeSelection(deselectedRows);

    // Check if all rows are selected
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (rowSelection?.length === this.rows.length) {
      this.setSelectAll(true);
    }
    // Check if all rows are deselected
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    else if (rowSelection?.length === 0) {
      this.setSelectAll(false);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.rowSelection = rowSelection;

    this.updateBulkEditSelectedValue();
  }

  /**
   * Bulk edit toggle RateInsight
   */
  @action
  toggleRateInsight(): void {
    if (this.rateInsightIndeterminate) {
      this.rateInsight = true;
      this.rateInsightIndeterminate = false;
    } else {
      this.rateInsight = !this.rateInsight;
    }

    this.hasUnsavedRateInsightBulkEditValues = true;

    this.syncRateInsightAccessStatus();
  }

  /**
   * Bulk edit toggle RevenueInsight
   */
  @action
  toggleRevenueInsight(): void {
    if (this.revenueInsightIndeterminate) {
      this.revenueInsight = true;
      this.revenueInsightIndeterminate = false;
    } else {
      this.revenueInsight = !this.revenueInsight;
    }

    this.hasUnsavedRevenueInsightBulkEditValues = true;

    this.syncRevenueInsightAccessStatus();
  }

  <template>
    {{! @glint-expect-error: Incorrect type }}
    <PrismModal @onClose={{@redirectOnClose}} @title={{@editText}}>
      <:default>
        <div>
          {{! @glint-expect-error: Incorrect type }}
          {{#if @model.isLoading}}
            <div class="progress-bar__wrapper">
              <PrismLoadingBar />
            </div>
          {{/if}}
          <div class="hotel-access-hotel-search position-relative">
            <div class="hotel-access-hotel-multiselect">
              <div class="hotel-access-hotels-select flex flex-col">
                <label class="input-label">
                  {{t "useraccess_rights_add_hotel"}}
                </label>
                {{! @glint-expect-error: Incorrect type }}
                <GeneralDropdownsMultipleSelectDropdownAjax
                  @canSelectAll={{not this.access.isStaffUser}}
                  @isDisabled={{not this.canAddHotels}}
                  @onChange={{this.handleHotelsChange}}
                  @onLoadMore={{this.handleLoadMoreHotels}}
                  @options={{this.hotelOptions}}
                  @totalCount={{this.totalCount}}
                  @translationPrefix="useraccess_rights_hoteldropdown"
                  @value={{this.hotels}}
                  @visibleItems={{this.visibleItems}}
                  data-test-id="hotel-access-hotel-select"
                />
              </div>
              <div class="hotel-access-hotels-success">
                {{#if this.showSuccessMessage}}
                  <span><PrismIcon
                      @color="success"
                      @name="checkmark"
                      @size="small"
                    /></span>
                  <p class="text-300--regular">
                    {{t "useraccess_rights_successmessage"}}</p>
                {{/if}}
              </div>
            </div>
            <div class="hotel-access-hotel">
              <div class="hotel-access-hotel__searchbar flex flex-col">
                <PrismInput
                  @iconAfter={{hash name="search"}}
                  @onInput={{this.onInput}}
                  @placeholder={{t "useraccess_rights_search_placeholder"}}
                  @value={{this.searchText}}
                />
              </div>
              <div class="hotel-access-hotel__content flex flex-col">
                <PrismTable
                  @checkboxSelectionMode="multiple"
                  @columns={{this.columns}}
                  @fillColumnIndex={{1}}
                  @fillMode="nth-column"
                  @isLoading={{this.isLoading}}
                  @isLoadingMore={{this.isLoadingMore}}
                  @lastReached={{this.onEndReached}}
                  @onRowSelect={{this.setRowSelection}}
                  @onUpdateSorts={{this.onUpdateSorts}}
                  @oneCellBulkEditRow={{false}}
                  @overlayMessage={{this.noResultsMessage}}
                  @removeTableBorder={{true}}
                  @rowSelection={{unless this.isLoading this.rowSelection}}
                  @rows={{this.rows}}
                  @sorts={{this.sorts}}
                  @widthConstraint="eq-container"
                  class="hotel-access-hotel__content__table"
                >
                  <:bulkEditRow>
                    <th class="bulk-edit-cell" colspan="2">
                      <div
                        class="hotel-access-hotel-search__selected__badge pl-500"
                      >
                        <PrismBadge @type="info">
                          <span class="pr-200 font-bold">
                            {{this.selectedRowCount}}
                          </span>
                          {{t "settings.hotel_access.selected"}}
                        </PrismBadge>
                      </div>
                    </th>
                    <th class="bulk-edit-cell">
                      <div
                        class="hotel-access-hotel-search__selected__dropdown flex flex-col"
                      >
                        <PrismSelectInput
                          @onChange={{this.multiRightsChanged}}
                          {{! @glint-expect-error: Incorrect type }}
                          @options={{this.rights}}
                          {{! @glint-expect-error: Incorrect type }}
                          @placeholder={{this.selectedRight}}
                          @value={{this.selectedRight}}
                        />
                      </div>
                    </th>
                    <th class="bulk-edit-cell">
                      <div
                        class="hotel-access-hotel-search__selected__checkbox"
                      >
                        {{#if this.showRateInsight}}
                          <PrismCheckbox
                            @checked={{this.rateInsight}}
                            @disabled={{this.isRateInsightBulkCheckboxDisabled}}
                            @indeterminate={{this.rateInsightIndeterminate}}
                            @onChange={{this.toggleRateInsight}}
                          />
                        {{/if}}
                      </div>
                    </th>
                    <th class="bulk-edit-cell">
                      <div
                        class="hotel-access-hotel-search__selected__checkbox"
                      >
                        {{#if this.showRevenueInsight}}
                          <PrismCheckbox
                            @checked={{this.revenueInsight}}
                            @disabled={{this.isRevenueInsightBulkCheckboxDisabled}}
                            @indeterminate={{this.revenueInsightIndeterminate}}
                            @onChange={{this.toggleRevenueInsight}}
                          />
                        {{/if}}
                      </div>
                    </th>
                    <th class="bulk-edit-cell">
                      <div
                        class="hotel-access-hotel-search__selected__delete delete"
                      >
                        <PrismButton
                          @buttonType="ghost"
                          @icon="bin"
                          @onClick={{this.bulkDelete}}
                          @size="small"
                        />
                      </div>
                    </th>
                  </:bulkEditRow>
                </PrismTable>
              </div>
            </div>
          </div>
        </div>
      </:default>
      <:footer>
        <div class="hotel-access-save-cancel">
          <GeneralFormFieldsCancelAndConfirm
            @cancel={{this.cancelBulkEdit}}
            @confirm={{this.saveBulkEdit}}
            @confirmText={{t "general.save"}}
            @isCancelDisabled={{not this.inEditMode}}
            @isConfirmDisabled={{not this.inEditMode}}
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
